import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import GoogleLogo from './GoogleLogo.svg';

// Branding-visible styles (font, colours, border, sizing) are intentionally hardcoded
// to match Google's official "Sign in with Google" button guidelines
// (https://developers.google.com/identity/branding-guidelines).
// Values must not use VerifiMe design tokens. Internal elements (e.g. loading spinner)
// may use theme tokens.
export type SignInWithGoogleButtonProps = {
  loading?: boolean;
} & Omit<ButtonProps, 'variant' | 'size' | 'startIcon' | 'children'>;

export default function SignInWithGoogleButton({ loading = false, sx, ...props }: SignInWithGoogleButtonProps) {
  const theme = useTheme();

  return (
    <Button
      variant="outlined"
      size="large"
      startIcon={
        loading ? (
          <CircularProgress size={theme.semantic.fontSize.base} color="inherit" />
        ) : (
          <GoogleLogo width={18} height={18} />
        )
      }
      {...props}
      disabled={loading || props.disabled}
      data-action="sign-in-with-google"
      sx={[
        {
          backgroundColor: '#ffffff',
          borderColor: '#747775',
          color: '#1f1f1f',
          textTransform: 'none' as const,
          fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
          fontSize: '14px',
          fontWeight: 500,
          borderRadius: '4px',
          height: '40px',
          px: '12px',
          minWidth: 0,
          letterSpacing: '0.25px',
          '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: '#5e5e5e',
            boxShadow: '0 1px 2px rgba(60,64,67,0.30)',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {loading ? 'Redirecting...' : 'Continue with Google'}
    </Button>
  );
}
